import logger from '@eventbrite/client-logger';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { unformat } from '@eventbrite/intl';
import { Dispatch } from 'redux';
import { ApplePayWithMerchantIdentifier } from '../actions/applePay';
import {
    trackMissingContactInfoInApplePayFixable,
    trackMissingContactInfoInApplePayFixableWithOtherField,
    trackMissingContactInfoInApplePayNonFixable,
} from '../actions/eventAnalytics';
import { APPLE_PAY_FIELD } from '../constants/applePay';
import { ContactFields } from '../models/order';
import {
    areAsyncExpressCheckoutEligibleRulesMet,
    areSyncExpressCheckoutEligibleRulesMet,
} from '../selectors/applePay';
import { getBreakdownItems } from '../selectors/tickets';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { OrderNamespace } from '../state/order';
import { UserNamespace } from '../state/user';
interface ApplePayLineItem {
    label: string;
    amount: string;
}

/**
 * @WARNING This will throw an error in Safari on organizer sites.
 */
export const isApplePaySupported = () => {
    try {
        return (
            HAS_WINDOW &&
            window.ApplePaySession?.supportsVersion(3) &&
            window.ApplePaySession?.canMakePayments()
        );
    } catch (e) {
        logger.error(e);
        return false;
    }
};

/**
 * @WARNING This will throw an error in Safari on organizer sites.
 */
export const isApplePayCardActive = async (
    applePayInstance: ApplePayWithMerchantIdentifier,
) => {
    try {
        return (
            isApplePaySupported() &&
            (await window.ApplePaySession?.canMakePaymentsWithActiveCard(
                applePayInstance.merchantIdentifier,
            ))
        );
    } catch (e) {
        logger.error(e);
        return false;
    }
};

const getSlicedContactInfo = (fieldValue: string) => {
    return {
        firstName: fieldValue.slice(0, fieldValue.indexOf(' ')),
        lastName: fieldValue.slice(fieldValue.indexOf(' ') + 1),
    };
};

const getPartialValues = (
    dispatch: Dispatch<CheckoutState>,
    definedFieldValue: string,
    missingFieldName: APPLE_PAY_FIELD,
) => {
    const targetFieldValueWords = definedFieldValue.split(' ');

    if (targetFieldValueWords?.length > 1) {
        dispatch(
            trackMissingContactInfoInApplePayFixableWithOtherField(
                missingFieldName,
            ),
        );

        return getSlicedContactInfo(definedFieldValue);
    }

    dispatch(trackMissingContactInfoInApplePayNonFixable(missingFieldName));

    return null;
};

const getSanitisedContactInfoField = (
    dispatch: Dispatch<CheckoutState>,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    user?: UserNamespace,
) => {
    const { givenName, familyName } = event.payment?.shippingContact || {};
    const { firstName, lastName } = user || {};

    if (givenName && familyName) {
        return {
            firstName: givenName,
            lastName: familyName,
        };
    }

    if (firstName && lastName) {
        dispatch(
            trackMissingContactInfoInApplePayFixable(
                !givenName
                    ? APPLE_PAY_FIELD.GIVEN_NAME
                    : APPLE_PAY_FIELD.FAMILY_NAME,
            ),
        );

        return {
            firstName,
            lastName,
        };
    }

    if (!familyName && givenName) {
        return getPartialValues(
            dispatch,
            givenName,
            APPLE_PAY_FIELD.FAMILY_NAME,
        );
    }

    if (!givenName && familyName) {
        return getPartialValues(
            dispatch,
            familyName,
            APPLE_PAY_FIELD.GIVEN_NAME,
        );
    }

    dispatch(
        trackMissingContactInfoInApplePayNonFixable(
            `${APPLE_PAY_FIELD.GIVEN_NAME}|${APPLE_PAY_FIELD.FAMILY_NAME}`,
        ),
    );
    return null;
};

export const getApplePayBuyerData = (
    dispatch: Dispatch<CheckoutState>,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    user?: UserNamespace,
) => {
    const emailAddress =
        event.payment?.shippingContact?.emailAddress ?? user?.email;
    const contactInfo = getSanitisedContactInfoField(dispatch, event, user);

    return {
        [ContactFields.FIRST_NAME]: contactInfo?.firstName ?? null,
        [ContactFields.LAST_NAME]: contactInfo?.lastName ?? null,
        [ContactFields.EMAIL]: emailAddress ?? null,
    };
};

export const getApplePayBreakdownItems = (
    state: CheckoutState & {
        order: OrderNamespace;
        event: EventNamespace;
    },
): ApplePayLineItem[] => {
    return getBreakdownItems(state).map((item) => ({
        label: item.content.toString(),
        amount: unformat(item.value, state.app.currencyFormat),
    }));
};

export const isExpressApplePayEnabled = (state: CheckoutState) =>
    areSyncExpressCheckoutEligibleRulesMet(state) &&
    areAsyncExpressCheckoutEligibleRulesMet(state);
